import React, { useState } from 'react'
import { ActionPlan, RiskAnalysis, RiskAnalysisStatus } from '../../../api/schemas/schema'
import { findSWOTParentToActionPlan, getGroupedSWOTActionPlans, getSWOTActionPlanColumns, getSWOTActionPlanSummaryFields } from '../../../helpers/swotHelper'
import { Card } from '../../common/card/Card'
import { t } from '../../../i18n/i18n'
import { Button, makeStyles, tokens, Text, MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-components'
import { PhoneHeader } from '../../common/header/PhoneHeader'
import { ArrowExport20Regular } from '@fluentui/react-icons'
import { BadgeActionPlanStatus } from '../../demandAnalysis/BadgeActionPlanStatus'
import { formatToSwedishCrowns } from '../../../helpers/stringHelper'
import { GroupedList } from '../../common/list/GroupedList'
import { ModalActionPlan } from '../../demandAnalysis/actionPlan/ModalActionPlan'
import { ParentSWOTInformation } from './ParentSWOTInformation'
import { exportSWOTActionPlansToExcel } from '../../../helpers/excelHelper'
import { updateActionPlan } from '../../../helpers/riskAnalysisHelper'
import { IItemState } from '../../../interfaces/IItemState'
const useStyles = makeStyles({
   hideInPhone: {
      '@media screen and (max-width: 1007px)': {
         display: 'none',
      },
   },
   spaceBtw: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
   },
   ellipsis: {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'normal',
      WebkitLineClamp: 3,
   },
   phoneItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalM,
   },
   exportBtn: {
      float: 'right',
   },
   wrapText: {
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      whiteSpace: 'normal',
   },
})
type SWOTActionPlansProps = {
   setRiskAnalysisState: React.Dispatch<React.SetStateAction<IItemState<RiskAnalysis>>>
   analysisState: IItemState<RiskAnalysis>
}
export const SWOTActionPlans = ({ setRiskAnalysisState, analysisState }: SWOTActionPlansProps) => {
   const classes = useStyles()
   const title = t('RiskManagementPlan')
   const [selectedItem, setSelectedItem] = useState<ActionPlan>(null)
   const analysis = analysisState.item
   const disabled = analysisState.disabled || analysis.status === RiskAnalysisStatus.Completed

   const saveActionPlan = async (data: ActionPlan) => {
      return await updateActionPlan(data, analysis, setRiskAnalysisState)
   }

   return (
      <>
         <PhoneHeader title={title} />
         <Card className={classes.hideInPhone} transparentInPhone title={title}>
            <Button
               icon={<ArrowExport20Regular />}
               className={classes.exportBtn}
               onClick={() => exportSWOTActionPlansToExcel(analysis, analysis.actionPlans)}
               disabled={analysis.actionPlans.length === 0}
            >
               {t('ExportToExcel')}
            </Button>
         </Card>
         {analysis.actionPlans.length > 0 ? (
            <GroupedList
               columns={getSWOTActionPlanColumns(analysis.swoTs)}
               groups={getGroupedSWOTActionPlans(analysis.swoTs, analysis.actionPlans)}
               onRowClick={(item) => setSelectedItem(item)}
               summaryFields={getSWOTActionPlanSummaryFields(analysis.actionPlans, analysis.swoTs)}
               renderPhoneRow={(item) => {
                  const swot = findSWOTParentToActionPlan(analysis.swoTs, item)
                  return (
                     <div className={classes.phoneItem}>
                        <span className={classes.spaceBtw}>
                           <Text size={400}>{swot.title}</Text>
                           <BadgeActionPlanStatus status={item.status} />
                        </span>
                        <Text size={300}>{swot.estimatedCost || swot.estimatedCost === 0 ? formatToSwedishCrowns(swot.estimatedCost) + ' kr' : null}</Text>
                        <Text size={300} className={classes.ellipsis}>
                           {swot.description}
                        </Text>
                     </div>
                  )
               }}
            />
         ) : (
            <MessageBar intent={'info'}>
               <MessageBarBody>
                  <MessageBarTitle className={classes.wrapText}>
                     {analysis.swoTs.length > 0 && analysis.actionPlans.length === 0
                        ? t('NoActionPlansRequiredForThisItem', { Item: t('RiskAssesment').toLowerCase() })
                        : t('TimeToAddYourFirstSWOT')}
                  </MessageBarTitle>
                  {analysis.swoTs.length > 0 && t('RiskAssessmentCanBeCompleted')}
               </MessageBarBody>
            </MessageBar>
         )}
         <ModalActionPlan
            parentInformation={selectedItem && <ParentSWOTInformation swot={analysis.swoTs.find((x) => x.id === selectedItem.swotId)} swots={analysis.swoTs} />}
            selectedItem={selectedItem}
            close={() => {
               setSelectedItem(undefined)
            }}
            estimatedCost={selectedItem && analysis.swoTs.find((x) => x.id === selectedItem?.swotId)?.estimatedCost}
            disabled={disabled}
            onSubmit={saveActionPlan}
         />
      </>
   )
}
